/**
 * Servicio para descargar archivos de Google Drive usando la API directamente
 */

const GOOGLE_DRIVE_CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID || '';
const GOOGLE_DRIVE_CLIENT_SECRET = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_SECRET || '';
const GOOGLE_DRIVE_REFRESH_TOKEN = process.env.REACT_APP_GOOGLE_DRIVE_REFRESH_TOKEN || '';

// Debug: Verificar que las variables de entorno se están leyendo (solo en desarrollo)
if (process.env.NODE_ENV === 'development') {
  console.log('Google Drive Config Check:', {
    hasClientId: !!GOOGLE_DRIVE_CLIENT_ID,
    hasClientSecret: !!GOOGLE_DRIVE_CLIENT_SECRET,
    hasRefreshToken: !!GOOGLE_DRIVE_REFRESH_TOKEN,
    clientIdPrefix: GOOGLE_DRIVE_CLIENT_ID ? GOOGLE_DRIVE_CLIENT_ID.substring(0, 10) + '...' : 'missing',
    refreshTokenPrefix: GOOGLE_DRIVE_REFRESH_TOKEN ? GOOGLE_DRIVE_REFRESH_TOKEN.substring(0, 10) + '...' : 'missing'
  });
}

// Cache para el access token
let cachedAccessToken = null;
let tokenExpiryTime = 0;

/**
 * Obtiene un access token usando el refresh token directamente desde el frontend
 */
async function getAccessToken() {
  // Si tenemos un token válido en cache, usarlo
  if (cachedAccessToken && Date.now() < tokenExpiryTime) {
    return cachedAccessToken;
  }

  try {
    // Verificar que las credenciales estén disponibles
    if (!GOOGLE_DRIVE_CLIENT_ID || !GOOGLE_DRIVE_CLIENT_SECRET || !GOOGLE_DRIVE_REFRESH_TOKEN) {
      console.error('Google Drive credentials missing:', {
        hasClientId: !!GOOGLE_DRIVE_CLIENT_ID,
        hasClientSecret: !!GOOGLE_DRIVE_CLIENT_SECRET,
        hasRefreshToken: !!GOOGLE_DRIVE_REFRESH_TOKEN,
        clientIdLength: GOOGLE_DRIVE_CLIENT_ID?.length || 0,
        clientSecretLength: GOOGLE_DRIVE_CLIENT_SECRET?.length || 0,
        refreshTokenLength: GOOGLE_DRIVE_REFRESH_TOKEN?.length || 0
      });
      throw new Error('Google Drive credentials not configured. Please check your environment variables.');
    }

    // Obtener access token directamente desde Google OAuth2
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: GOOGLE_DRIVE_CLIENT_ID,
        client_secret: GOOGLE_DRIVE_CLIENT_SECRET,
        refresh_token: GOOGLE_DRIVE_REFRESH_TOKEN,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Error al obtener access token: ${error}`);
    }

    const data = await response.json();
    if (!data.access_token) {
      throw new Error('No se recibió access_token en la respuesta');
    }
    const accessToken = data.access_token;
    cachedAccessToken = accessToken;
    // El token expira en 1 hora, pero lo refrescamos 5 minutos antes
    tokenExpiryTime = Date.now() + (data.expires_in - 300) * 1000;

    return accessToken;
  } catch (error) {
    throw error;
  }
}

/**
 * Descarga un archivo de Google Drive usando su ID directamente desde la API
 * @param {string} fileId ID del archivo en Google Drive
 * @returns {Promise<Blob>} Blob con el contenido del archivo
 */
export async function downloadGoogleDriveFile(fileId) {
  try {
    console.log('Downloading Google Drive file:', fileId);
    const accessToken = await getAccessToken();
    console.log('Access token obtained successfully');

    // Usar directamente la API de Google Drive
    // GET https://www.googleapis.com/drive/v3/files/{fileId}?alt=media
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      console.error('Error downloading file from Google Drive:', {
        status: response.status,
        statusText: response.statusText,
        error: error,
        fileId: fileId
      });
      throw new Error(`Error al descargar archivo: ${error}`);
    }
    
    console.log('File downloaded successfully from Google Drive');

    // Leer como array buffer primero para verificar el contenido
    const arrayBuffer = await response.arrayBuffer();
    
    // Detectar el tipo de archivo por los primeros bytes (magic numbers)
    const uint8Array = new Uint8Array(arrayBuffer);
    let mimeType = response.headers.get('content-type') || '';
    
    // Si no hay content-type, detectar por magic numbers
    if (!mimeType || mimeType === 'application/octet-stream') {
      // Leer los primeros bytes como texto para detectar SVG
      const textDecoder = new TextDecoder();
      const firstBytes = textDecoder.decode(uint8Array.slice(0, Math.min(100, uint8Array.length)));
      
      if (firstBytes.trim().startsWith('<?xml') || firstBytes.trim().startsWith('<svg') || firstBytes.trim().startsWith('<!DOCTYPE svg')) {
        mimeType = 'image/svg+xml';
      } else if (uint8Array.length >= 3 && uint8Array[0] === 0xFF && uint8Array[1] === 0xD8 && uint8Array[2] === 0xFF) {
        mimeType = 'image/jpeg';
      } else if (uint8Array.length >= 8 && 
                 uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
        mimeType = 'image/png';
      } else if (uint8Array.length >= 4 && 
                 uint8Array[0] === 0x47 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46) {
        mimeType = 'image/gif';
      } else {
        // Por defecto, intentar SVG si es pequeño (probablemente SVG)
        if (arrayBuffer.byteLength < 10000) {
          mimeType = 'image/svg+xml';
        } else {
          mimeType = 'image/jpeg';
        }
      }
    }
    
    const blob = new Blob([arrayBuffer], { type: mimeType });
    
    return blob;
  } catch (error) {
    throw error;
  }
}

/**
 * Descarga un archivo de Google Drive y lo convierte a base64
 * @param {string} fileId ID del archivo en Google Drive
 * @returns {Promise<string>} Base64 data URL para mostrar la imagen
 */
export async function downloadGoogleDriveFileAsBlobUrl(fileId) {
  const blob = await downloadGoogleDriveFile(fileId);
  
  // Siempre convertir a base64 para evitar problemas con blob URLs
  // Esto es más confiable y funciona mejor con SVGs y otros formatos
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
}
